import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  PlusIcon,
  ArrowLeftIcon,
  ChevronDownIcon,
  FunnelIcon,
} from "@heroicons/react/24/outline";
import axios from "../../../api/axios";
import { mapErrors, safeString } from "../../Utils";
import { SelectorListSkeleton } from "../ResourceLoading";
import { PMField } from "../PMField";
import { PMDialog } from "../PMDialog";
import { NorwegianPhoneNumberSchema } from "../../validations";
import { CustomerSelector } from "./customerSelector";
import { useAutofocusOnOpen } from "../useAutofocusOnOpen";

const FETCH_URL = "/contacts/";

export const ContactsSelector = ({ showField = true, noLabel, ...props }) => {
  const {
    setFieldValue,
    values,
    onChange,
    errors,
    module: _module,
    module_param,
    fields,
    customer,
  } = props;

  const name = props?.name ?? "contact_id";
  const label = props?.label ?? "Contact";

  const { t } = useTranslation();

  // ── Customer filter ───────────────────────────────────────────────────────
  const customerId = customer?.id || module_param || values?.customer_id;
  const [filterByCustomer, setFilterByCustomer] = useState(!!customerId);

  useEffect(() => {
    setFilterByCustomer(
      !!(customer?.id || module_param || values?.customer_id)
    );
  }, [customer?.id, module_param, values?.customer_id]);

  // ── Dialog state ──────────────────────────────────────────────────────────
  const [open, setOpen] = useState(false);
  const [inCreateView, setInCreateView] = useState(false);

  // `startInCreateView` lets the new pickers open this selector straight in
  // its create form ("new customer/contact") without changing default use.
  useEffect(() => {
    if (props.open === true) {
      setOpen(true);
      setInCreateView(props.startInCreateView === true);
    } else if (props.open === false) {
      setOpen(false);
      setInCreateView(false);
    }
  }, [props.open]);

  // ── Search & list state ───────────────────────────────────────────────────
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const searchInputRef = useAutofocusOnOpen(open && !inCreateView);

  // ── Selected state ────────────────────────────────────────────────────────
  const [selected, setSelected] = useState({
    id: null,
    field1: "Ingen valgt",
  });

  useEffect(() => {
    if (props?.selected?.id) {
      setSelected({
        id: props.selected.id,
        field1: safeString(
          props.selected?.displayTitle ?? props.selected?.name,
          "Ingen valgt"
        ),
      });
    } else if (!props?.selected?.id) {
      setSelected({ id: null, field1: "Ingen valgt" });
    }
  }, [props.selected]);

  // ── Open/close ────────────────────────────────────────────────────────────
  const handleOpen = () => {
    if (props.disabled) return;
    setOpen(true);
    setInCreateView(false);
  };

  const handleClose = () => {
    setOpen(false);
    setInCreateView(false);
    setQuery("");
    props.onClose?.(false);
  };

  // ── Propagate selection ───────────────────────────────────────────────────
  const applySelection = (id, item) => {
    if (values) setFieldValue(name, id);
    if (onChange) onChange({ target: { name, value: id, data: item } });
  };

  const handleSelect = (item) => {
    setSelected({
      id: item.id,
      field1: safeString(item.primary, "Ingen valgt"),
    });
    applySelection(item.id, item.object ?? item);
    handleClose();
  };

  const handleSelectFromForm = (data) => {
    setSelected({
      id: data?.id,
      field1: safeString(data?.displayTitle ?? data?.name, "Ingen valgt"),
    });
    applySelection(data?.id, data);
    handleClose();
  };

  // ── Data fetching ─────────────────────────────────────────────────────────
  const fetchItems = useCallback(
    async (searchQuery, byCustomer) => {
      setLoading(true);
      const cid = customer?.id || module_param || values?.customer_id;
      try {
        const filters =
          byCustomer && cid ? [{ name: "CUSTOMER", value: cid }] : [];
        const response = await axios({
          method: "get",
          url: FETCH_URL,
          params: {
            query: searchQuery || undefined,
            page: 1,
            per_page: 20,
            sortBy: "created_at",
            sort: "desc",
            filters: filters.length ? filters : undefined,
          },
        });

        const array = [
          { id: null, value: null, primary: "Ingen valgt", secondary: null },
        ];
        if (response.data?.data?.length > 0) {
          response.data.data.forEach((row) => {
            array.push({
              id: row.id,
              value: row.id,
              primary: safeString(
                row?.displayTitle ?? row?.name,
                "Ingen valgt"
              ),
              secondary: row?.customer
                ? safeString(
                    row.customer?.displayTitle ?? row.customer?.name,
                    "Ikke tilknyttet kunde"
                  )
                : null,
              object: row,
            });
          });
        }
        setItems(array);
      } catch {
        setItems([
          { id: null, value: null, primary: "Ingen valgt", secondary: null },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [customer?.id, module_param, values?.customer_id]
  );

  const debouncedFetch = useCallback(
    debounce((q, byCustomer) => fetchItems(q, byCustomer), 300),
    [fetchItems]
  );

  useEffect(() => {
    if (!open) return;
    debouncedFetch(query, filterByCustomer);
    return () => debouncedFetch.cancel();
  }, [query, open, filterByCustomer, debouncedFetch]);

  // ── Create form ───────────────────────────────────────────────────────────
  const createOrUpdate = (vals) => {
    const data = { ...vals, customer_id: vals.customer_id || module_param };
    return axios.post(FETCH_URL, data).then((r) => r);
  };

  const renderCreateForm = () => (
    <Formik
      initialValues={{
        id: null,
        customer_id: customerId || null,
        name: fields?.name ?? "",
        mobile: fields?.mobile ?? "",
        email: fields?.email ?? "",
        submit: null,
      }}
      validationSchema={Yup.object({
        name: Yup.string().required(t("Name is required")),
        email: Yup.string().required(t("Email is required")),
        mobile: NorwegianPhoneNumberSchema,
      })}
      onSubmit={(vals, helpers) => {
        try {
          toast.promise(createOrUpdate(vals), {
            loading: t("Saving...."),
            success: (result) => {
              const payload = result?.data ?? result;
              if (payload?.errors) throw new Error(mapErrors(payload.errors));
              fetchItems(query, filterByCustomer);
              if (payload?.id) handleSelectFromForm(payload);
              return t("Data was saved!");
            },
            error: () => t("Something went wrong. Could not save data!"),
          });
          helpers.setStatus({ success: true });
          helpers.setSubmitting(false);
        } catch (err) {
          toast.error("Something went wrong...");
          helpers.setStatus({ success: false });
          helpers.setErrors({ submit: err.message });
          helpers.setSubmitting(false);
        }
      }}
    >
      {(formik) => (
        <Form className="space-y-4 p-4">
          <CustomerSelector
            label="Customer"
            name="customer_id"
            {...formik}
          />
          <p className="text-xs text-gray-500 -mt-2">
            {t("contact.customer.explanation")}
          </p>
          <PMField name="name" type="text" label={t("Name")} {...formik} />
          <PMField name="mobile" type="text" label={t("Mobile")} {...formik} />
          <PMField name="email" type="text" label={t("Email")} {...formik} />
          <div className="pt-2">
            <button
              type="button"
              onClick={formik.submitForm}
              disabled={formik.isSubmitting}
              className="w-full inline-flex justify-center rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
            >
              {t("create")}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );

  // ── Trigger button ────────────────────────────────────────────────────────
  const hasSelection = selected?.id != null;

  const triggerButton = showField ? (
    <div>
      {noLabel !== false && (
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {t(label)}
      </label>
      )}
      <button
        type="button"
        disabled={props.disabled}
        onClick={handleOpen}
        className={[
          "relative w-full flex items-center justify-between",
          "rounded-md border bg-white px-3 py-2 text-sm shadow-sm",
          "transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500",
          props.disabled
            ? "cursor-not-allowed border-gray-200 text-gray-400"
            : hasSelection
            ? "border-blue-400 text-gray-900 hover:border-blue-500"
            : "border-gray-300 text-gray-500 hover:border-gray-400",
        ].join(" ")}
      >
        <span className="truncate">{selected.field1}</span>
        <ChevronDownIcon className="h-4 w-4 flex-shrink-0 text-gray-400 ml-2" />
      </button>
      {errors?.[name] && (
        <p className="mt-1 text-xs text-red-600">{errors[name]}</p>
      )}
    </div>
  ) : null;

  // ── Dialog content ────────────────────────────────────────────────────────
  const dialogContent = (
    <div className="flex flex-col h-full" style={{ minHeight: 0 }}>
      {/* Header */}
      <div className="flex-shrink-0 p-3 sm:p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          {inCreateView ? (
            <button
              type="button"
              onClick={() => setInCreateView(false)}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Tilbake
            </button>
          ) : (
            <h3 className="text-base font-semibold text-gray-900">
              {t(label)}
            </h3>
          )}
          <button
            type="button"
            onClick={handleClose}
            className="rounded-md p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {!inCreateView && (
          <>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
              </div>
              <input
                ref={searchInputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Søk etter kontakt..."
                autoFocus
                className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-9 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="absolute inset-y-0 right-0 flex items-center pr-3"
                >
                  <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                </button>
              )}
            </div>

            {customerId && (
              <div className="mt-2 flex items-center gap-2">
                <FunnelIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                <button
                  type="button"
                  onClick={() => setFilterByCustomer((v) => !v)}
                  className={[
                    "px-2.5 py-1 text-xs rounded-md transition-colors",
                    filterByCustomer
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200",
                  ].join(" ")}
                >
                  Knyttet til kunde
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto" style={{ minHeight: 0 }}>
        {inCreateView ? (
          renderCreateForm()
        ) : loading ? (
          <SelectorListSkeleton />
        ) : (
          <ul className="divide-y divide-gray-100">
            {items.map((item) => {
              const isSelected = item.id === selected?.id;
              return (
                <li
                  key={item.id ?? "none"}
                  onClick={() => handleSelect(item)}
                  className={[
                    "flex items-center justify-between px-4 py-3 cursor-pointer transition-colors",
                    isSelected
                      ? "bg-blue-50 text-blue-700"
                      : "hover:bg-gray-50 text-gray-900",
                  ].join(" ")}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{item.primary}</p>
                    {item.secondary && (
                      <p className="text-xs text-gray-500 truncate mt-0.5">
                        {item.secondary}
                      </p>
                    )}
                  </div>
                  {isSelected && (
                    <CheckIcon className="h-4 w-4 flex-shrink-0 ml-3 text-blue-600" />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Footer */}
      {!inCreateView && (
        <div className="flex-shrink-0 border-t border-gray-200 p-3">
          <button
            type="button"
            onClick={() => setInCreateView(true)}
            className="flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-gray-300 px-4 py-2.5 text-sm text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors"
          >
            <PlusIcon className="h-4 w-4" />
            Opprett ny kontakt
          </button>
        </div>
      )}
    </div>
  );

  return (
    <>
      {triggerButton}
      <PMDialog open={open} onClose={handleClose} size="md" dense zIndex={3000} closeOnOutsideClick>
        {dialogContent}
      </PMDialog>
    </>
  );
};
